import { useState, useEffect, useCallback } from 'react';
import { Monitor, MonitorStatus, CreateMonitorRequest, UpdateMonitorRequest } from '../types';
import { monitorApi, statusApi } from '../utils/api';
import { useSocket } from './useSocket';
import { PingUpdateEvent, StatusChangeEvent } from '../types';

interface UseMonitorsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  /**
   * false = do not fetch. Monitor endpoints are admin-only, so a viewer account
   * must not call them (it would only paint a 403 banner on the dashboard).
   */
  enabled?: boolean;
}

interface UseMonitorsReturn {
  monitors: Monitor[];
  monitorStatuses: MonitorStatus[];
  loading: boolean;
  error: string | null;
  refreshMonitors: () => Promise<void>;
  refreshStatuses: () => Promise<void>;
  createMonitor: (monitor: CreateMonitorRequest) => Promise<Monitor>;
  updateMonitor: (id: number, monitor: UpdateMonitorRequest) => Promise<Monitor>;
  deleteMonitor: (id: number) => Promise<void>;
  getMonitorStatus: (id: number) => MonitorStatus | undefined;
}

export const useMonitors = (options: UseMonitorsOptions = {}): UseMonitorsReturn => {
  const { autoRefresh = true, refreshInterval = 30000, enabled = true } = options;

  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [monitorStatuses, setMonitorStatuses] = useState<MonitorStatus[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Socket connection for real-time updates
  const { isConnected, onPingUpdate, onStatusChange } = useSocket({
    onConnect: () => {
      console.log('Socket connected, refreshing monitors');
      refreshMonitors();
    },
    onError: (error) => {
      console.error('Socket error:', error);
      setError('Real-time connection failed');
    },
  });

  // Fetch all monitors
  const fetchMonitors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const monitorsData = await monitorApi.getMonitors();
      setMonitors(monitorsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch monitors';
      setError(errorMessage);
      console.error('Error fetching monitors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch monitor statuses
  const fetchStatuses = useCallback(async () => {
    try {
      const systemStatus = await statusApi.getSystemStatus();
      setMonitorStatuses(systemStatus.monitors_status);
    } catch (err) {
      console.error('Error fetching monitor statuses:', err);
      // Don't set error here as it's not critical for basic functionality
    }
  }, []);

  // Refresh monitors
  const refreshMonitors = useCallback(async () => {
    await fetchMonitors();
  }, [fetchMonitors]);

  // Refresh statuses
  const refreshStatuses = useCallback(async () => {
    await fetchStatuses();
  }, [fetchStatuses]);

  // Create monitor
  const createMonitor = useCallback(async (monitor: CreateMonitorRequest): Promise<Monitor> => {
    try {
      setError(null);
      const newMonitor = await monitorApi.createMonitor(monitor);
      setMonitors(prev => [newMonitor, ...prev]);
      return newMonitor;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create monitor';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Update monitor
  const updateMonitor = useCallback(async (id: number, monitor: UpdateMonitorRequest): Promise<Monitor> => {
    try {
      setError(null);
      const updatedMonitor = await monitorApi.updateMonitor(id, monitor);
      setMonitors(prev => prev.map(m => m.id === id ? updatedMonitor : m));
      return updatedMonitor;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update monitor';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Delete monitor
  const deleteMonitor = useCallback(async (id: number): Promise<void> => {
    try {
      setError(null);
      await monitorApi.deleteMonitor(id);
      setMonitors(prev => prev.filter(m => m.id !== id));
      setMonitorStatuses(prev => prev.filter(s => s.monitor?.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete monitor';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Get monitor status by ID
  const getMonitorStatus = useCallback((id: number): MonitorStatus | undefined => {
    return monitorStatuses.find(status => status.monitor?.id === id);
  }, [monitorStatuses]);

  // Handle real-time ping updates
  useEffect(() => {
    if (!isConnected) return;

    const handlePingUpdate = (event: PingUpdateEvent) => {
      setMonitorStatuses(prev => {
        const statusIndex = prev.findIndex(s => s.monitor?.id === event.monitor_id);
        if (statusIndex === -1) return prev;

        const updatedStatuses = [...prev];
        const status = { ...updatedStatuses[statusIndex] };
        
        // Update last ping
        status.last_ping = event.ping;
        status.is_currently_up = event.ping.is_up;
        
        // Recalculate uptime percentage (simplified - in real app you'd want to fetch fresh data)
        if (status.total_pings > 0) {
          const successfulPings = status.total_pings - Math.floor(status.total_pings * (100 - status.uptime_percentage) / 100);
          const newSuccessfulPings = successfulPings + (event.ping.is_up ? 1 : 0);
          status.uptime_percentage = Math.round((newSuccessfulPings / (status.total_pings + 1)) * 100);
          status.total_pings += 1;
        }

        updatedStatuses[statusIndex] = status;
        return updatedStatuses;
      });
    };

    const handleStatusChange = (event: StatusChangeEvent) => {
      console.log(`Monitor ${event.monitor_name} status changed from ${event.previous_status} to ${event.current_status}`);
      
      // You could show a toast notification here
      if (event.current_status === 'DOWN') {
        // Show alert for downtime
        console.warn(`🚨 ${event.monitor_name} is DOWN!`);
      }
    };

    onPingUpdate(handlePingUpdate);
    onStatusChange(handleStatusChange);

    return () => {
      // Cleanup listeners if needed
    };
  }, [isConnected, onPingUpdate, onStatusChange]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh || !enabled) return;

    const interval = setInterval(() => {
      refreshStatuses();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshStatuses]);

  // Initial data fetch
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchMonitors();
    fetchStatuses();
  }, [enabled, fetchMonitors, fetchStatuses]);

  return {
    monitors,
    monitorStatuses,
    loading,
    error,
    refreshMonitors,
    refreshStatuses,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    getMonitorStatus,
  };
}; 